import { faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@mui/material';
import { useContext, useState } from 'react';

import { CurrencyTypeEnum } from '../../model/currency_type.enum';
import { GlobalContext } from '../../providers/global-provider';
import { RequestTypeEnum } from '../../providers/timeseries-provider';
import { importPersistentStateFromOtherTab } from '../../providers/use-persistent-state';
import { Dropdown } from '../dropdown/dropdown';
import { Radio } from '../radio';
import './styles.scss';

const MARKET_DATA_HIDDEN_KEYS = ['token', 'type'];
interface IProps {
  onApply: (token: CurrencyTypeEnum, tenor: string, delta: string, type: RequestTypeEnum) => void;
  seriesExists: (
    token: CurrencyTypeEnum,
    tenor: string,
    delta: string,
    type?: RequestTypeEnum,
  ) => boolean;
  isMarketData?: boolean;
}

const AddSeriesMenu = (props: IProps) => {
  const { onApply, seriesExists, isMarketData = false } = props;
  const { marketDataOverviewCurrentTab: currentTab } = useContext(GlobalContext);

  const [open, setOpen] = useState(false);
  const [token, setToken] = useState(CurrencyTypeEnum.BITCOIN);
  const [tenor, setTenor] = useState<string | undefined>('1W');
  const [delta, setDelta] = useState<string | undefined>('ATMF');
  const [type, setType] = useState(RequestTypeEnum.VOLS);
  const [importToken, setImportToken] = useState<CurrencyTypeEnum | undefined>(undefined);

  const groupOptions = [
    {
      key: 'type',
      label: 'Type',
      options: [RequestTypeEnum.PERPETUAL, RequestTypeEnum.VOLS],
      action: (v: string) => setType(v as RequestTypeEnum),
      value: type,
    },
    {
      key: 'token',
      label: 'Token',
      options: [
        CurrencyTypeEnum.BITCOIN,
        CurrencyTypeEnum.ETHEREUM,
        CurrencyTypeEnum.XRP,
        CurrencyTypeEnum.SOL,
      ],
      action: (v: string) => setToken(v as CurrencyTypeEnum),
      value: token,
    },
    ...(type === RequestTypeEnum.VOLS
      ? [
          {
            key: 'tenor',
            label: 'Tenor',
            options: ['1W', '2W', '1M', '3M', '9M'],
            action: setTenor,
            value: tenor,
          },
          {
            key: 'delta',
            label: 'Delta',
            options: ['ATMF', '10P', '25P', '25C', '10C', '25RR', '10RR', '25FLY', '10FLY'],
            action: setDelta,
            value: delta,
          },
        ]
      : []),
  ];

  const renderMenu = () => {
    const onClose = () => {
      setOpen(false);
      setTenor('1W');
      setDelta('ATMF');
      setImportToken(undefined);
    };

    const handleOnApply = () => {
      if (tenor && delta) {
        const applyTenor = type === RequestTypeEnum.VOLS ? tenor : '';
        const applyDelta = type === RequestTypeEnum.VOLS ? delta : '';

        onApply(token, applyTenor, applyDelta, type);
      } else if (importToken) {
        importPersistentStateFromOtherTab(
          importToken,
          currentTab.replace('overview_', '').toUpperCase(),
        );
      }

      onClose();
    };

    const isSeriesExisting = !!tenor && !!delta && seriesExists(token, tenor, delta, type);

    return (
      <Dropdown isOpen={open} setIsOpen={setOpen} classNames="dropdown-class" onClose={onClose}>
        <div className="dropdown-menu-content">
          <h1 className="dropdown-title">Add Series</h1>

          {groupOptions.map((group) => {
            const { key, label, options, action, value } = group;

            if (MARKET_DATA_HIDDEN_KEYS.includes(key) && isMarketData) {
              return null;
            }

            return (
              <div key={key} className="dropdown-group">
                <span className="dropdown-group-title">{label}</span>

                <div className="group-options">
                  {options.map((option) => (
                    <Radio
                      key={option}
                      label={option.replace('_USDC', '')}
                      checked={value === option}
                      onChange={() => {
                        setImportToken(undefined);
                        action(option);
                      }}
                      className="group-option"
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        <div className="buttons">
          <Button className="cancel-button" onClick={onClose}>
            Cancel
          </Button>
          <Button className="apply-button" onClick={handleOnApply} disabled={isSeriesExisting}>
            Add
          </Button>
        </div>
      </Dropdown>
    );
  };

  return (
    <div className="add-series-menu-container">
      <Button className="add-series-button" onClick={() => setOpen(true)}>
        <FontAwesomeIcon icon={faPlusCircle} />
        <span>Add Series</span>
      </Button>
      {renderMenu()}
    </div>
  );
};

export default AddSeriesMenu;
